
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { saveAs } from "file-saver";

import Loader from "../../components/Loader";
import {
    EMPTY_PROFILE_DRAFT,
    deleteProfile,
    deleteProfileLogo,
    duplicateProfile,
    exportProfileJson,
    fetchProfileLogo,
    formatSchedule,
    getProfile,
    importSignJson,
    previewSign,
    readJsonFile,
    readApiError,
    toProfileDraft,
    updateProfile,
    uploadProfileLogo,
} from "./printSignApi";
import {
    Field,
    IconPicker,
    ItemsEditor,
    JsonImportButton,
    MAX_IMAGE_BYTES,
    ScheduleEditor,
    PreviewPane,
    Section,
    StringList,
    formatDateTime,
    sameDraft,
    useAuthImage,
    usePreview,
} from "./signUi";


const MAX_SERVICES = 16;
const MAX_FEATURES = 8;

/**
 * Редактор профиля бренда. Справа — предпросмотр на вывеске, где этот профиль
 * стоит (или на вывеске по умолчанию, если её ещё нет).
 */
const ProfileEditor = ({ profileId, meta, assets, signs, onSaved, onDeleted, onCreated }) => {
    const [profile, setProfile] = useState(null);
    const [draft, setDraft] = useState(EMPTY_PROFILE_DRAFT);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState("");
    const [message, setMessage] = useState(null);
    const mountedRef = useRef(true);
    const logoInputRef = useRef(null);

    useEffect(() => () => {
        mountedRef.current = false;
    }, []);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        setMessage(null);
        getProfile(profileId)
            .then((next) => {
                if (!alive) return;
                setProfile(next);
                setDraft(toProfileDraft(next));
            })
            .catch(async (error) => {
                if (alive) setMessage({ type: "error", text: await readApiError(error, "Профиль не открылся") });
            })
            .finally(() => alive && setLoading(false));
        return () => {
            alive = false;
        };
    }, [profileId]);

    const saved = useMemo(() => (profile ? toProfileDraft(profile) : null), [profile]);
    const dirty = !!saved && !sameDraft(draft, saved);
    const locked = !!busy;
    const iconAssets = assets.filter((asset) => asset.kind === "icon");
    const previewSignId = (signs.find((sign) => sign.profileId === profileId) || {})._id || "";

    const logoUrl = useAuthImage(
        profile?.hasLogo ? `logo:${profile._id}:${profile.logoUpdatedAt || ""}` : "",
        () => fetchProfileLogo(profile._id)
    );

    const patch = useCallback((changes) => {
        setDraft((current) => ({ ...current, ...changes }));
        setMessage(null);
    }, []);

    const preview = usePreview({
        enabled: !loading && !!profile,
        deps: { draft, logo: profile?.logoUpdatedAt, previewSignId },
        request: () => previewSign({ signId: previewSignId, profileId, profile: draft, width: 900 }),
    });

    const save = async () => {
        if (busy) return;
        setBusy("save");
        try {
            const next = await updateProfile(profileId, { ...draft, name: draft.name.trim() || draft.brandName || "Профиль бренда" });
            if (!mountedRef.current) return;
            setProfile(next);
            setDraft(toProfileDraft(next));
            onSaved(next);
            setMessage({ type: "success", text: "Профиль сохранён. Вывески с ним пересоберите, чтобы обновить файлы." });
        } catch (error) {
            if (mountedRef.current) setMessage({ type: "error", text: await readApiError(error, "Не удалось сохранить") });
        } finally {
            if (mountedRef.current) setBusy("");
        }
    };

    const pickLogo = async (file) => {
        if (!file || busy) return;
        if (file.size > MAX_IMAGE_BYTES) {
            setMessage({ type: "error", text: "Файл больше 30 МБ." });
            return;
        }
        setBusy("logo");
        try {
            const next = await uploadProfileLogo(profileId, file);
            if (!mountedRef.current) return;
            // Сервер меняет только логотип — правки на экране не трогаем.
            setProfile((current) => ({ ...current, hasLogo: next.hasLogo, logoUpdatedAt: next.logoUpdatedAt }));
            onSaved(next);
        } catch (error) {
            if (mountedRef.current) setMessage({ type: "error", text: await readApiError(error, "Не удалось загрузить логотип") });
        } finally {
            if (mountedRef.current) setBusy("");
        }
    };

    const removeLogo = async () => {
        if (busy) return;
        setBusy("logo");
        try {
            const next = await deleteProfileLogo(profileId);
            if (mountedRef.current) setProfile((current) => ({ ...current, hasLogo: next.hasLogo, logoUpdatedAt: next.logoUpdatedAt }));
        } catch (error) {
            if (mountedRef.current) setMessage({ type: "error", text: await readApiError(error, "Не удалось убрать логотип") });
        } finally {
            if (mountedRef.current) setBusy("");
        }
    };

    const exportJson = async () => {
        if (busy) return;
        setBusy("export");
        try {
            const { blob, filename } = await exportProfileJson(profileId);
            saveAs(blob, filename);
            if (dirty) setMessage({ type: "info", text: "В файл ушли сохранённые данные — несохранённые правки в него не попали." });
        } catch (error) {
            if (mountedRef.current) setMessage({ type: "error", text: await readApiError(error, "Не удалось выгрузить JSON") });
        } finally {
            if (mountedRef.current) setBusy("");
        }
    };

    /** Загрузить JSON в этот профиль: поля из файла заменяют текущие, остальное не трогается. */
    const importJson = async (file) => {
        if (busy) return;
        if (!window.confirm(`Загрузить «${file.name}» в профиль «${profile?.name}»? Поля из файла заменят текущие${dirty ? ", несохранённые правки пропадут" : ""}.`)) return;
        setBusy("import");
        setMessage(null);
        try {
            const result = await importSignJson({ data: await readJsonFile(file), profileId });
            if (!mountedRef.current) return;
            setProfile(result.profile);
            setDraft(toProfileDraft(result.profile));
            onSaved(result.profile);
            setMessage({
                type: result.notes?.length ? "info" : "success",
                text: result.notes?.length ? `Загружено. ${result.notes.join(" ")}` : "JSON загружен и сохранён в профиль.",
            });
        } catch (error) {
            if (mountedRef.current) setMessage({ type: "error", text: await readApiError(error, "Не удалось загрузить JSON") });
        } finally {
            if (mountedRef.current) setBusy("");
        }
    };

    const copy = async () => {
        if (busy) return;
        setBusy("copy");
        try {
            onCreated(await duplicateProfile(profileId));
        } catch (error) {
            if (mountedRef.current) setMessage({ type: "error", text: await readApiError(error, "Не удалось скопировать") });
        } finally {
            if (mountedRef.current) setBusy("");
        }
    };

    const remove = async () => {
        if (busy || !window.confirm(`Удалить профиль «${profile?.name}»?`)) return;
        setBusy("delete");
        try {
            await deleteProfile(profileId);
            onDeleted(profileId);
        } catch (error) {
            if (mountedRef.current) {
                setBusy("");
                setMessage({ type: "error", text: await readApiError(error, "Не удалось удалить") });
            }
        }
    };

    if (loading) return <div className="printCatalogs__placeholder"><Loader /></div>;
    if (!profile) return <div className="printCatalogs__placeholder">{message?.text || "Профиль не найден"}</div>;

    return (
        <>
            <div className="printCatalogs__editor">
                <div className="printCatalogs__form">
                    <Section title="Профиль бренда"
                        hint={`Из профиля берут данные все вывески с ним (потом и визитки). Создал: ${profile.createdByName || "—"} · ${formatDateTime(profile.createdAt)}`}
                        actions={(
                            <div className="printSigns__jsonButtons">
                                <JsonImportButton disabled={locked} onFile={importJson} label={busy === "import" ? "Загрузка…" : "Импорт JSON"} />
                                <button type="button" disabled={locked} onClick={exportJson}>{busy === "export" ? "…" : "Экспорт JSON"}</button>
                            </div>
                        )}>
                        <Field label="Название профиля (для себя)" value={draft.name} maxLength={120} disabled={locked}
                            placeholder="Например: Лик Памяти — Кировск" onChange={(name) => patch({ name })} />
                    </Section>

                    <Section title="Шапка" hint="Логотип, название фирмы и подпись под ним.">
                        <div className="printCatalogs__upload">
                            <div className="printCatalogs__uploadBox printSigns__logoBox">
                                {profile.hasLogo && logoUrl ? <img src={logoUrl} alt="" /> : <span>{profile.hasLogo ? "Загрузка…" : "Логотипа нет"}</span>}
                            </div>
                            <div className="printCatalogs__uploadActions">
                                <input ref={logoInputRef} type="file" accept="image/png,image/jpeg,image/webp" hidden
                                    onChange={(event) => {
                                        const file = event.target.files?.[0];
                                        event.target.value = "";
                                        pickLogo(file);
                                    }} />
                                <button type="button" disabled={locked} onClick={() => logoInputRef.current?.click()}>
                                    {busy === "logo" ? "Загрузка…" : profile.hasLogo ? "Заменить" : "Загрузить"}
                                </button>
                                {profile.hasLogo ? <button type="button" className="is-quiet" disabled={locked} onClick={removeLogo}>Убрать</button> : null}
                            </div>
                        </div>
                        <p className="printCatalogs__hint">Лучше PNG с прозрачным фоном. Если название уже нарисовано в логотипе — выключите «Название фирмы» в вывеске.</p>
                        <div className="printCatalogs__grid2">
                            <Field label="Название фирмы" value={draft.brandName} maxLength={80} disabled={locked}
                                placeholder="Лик Памяти" onChange={(brandName) => patch({ brandName })} />
                            <Field label="Подпись под названием" value={draft.tagline} maxLength={80} disabled={locked}
                                placeholder="Гранитная мастерская" onChange={(tagline) => patch({ tagline })} />
                        </div>
                    </Section>

                    <Section title="Контакты" hint="Сколько угодно телефонов, почт и адресов. Порядок — как на вывеске.">
                        <StringList label="Телефоны" values={draft.phones} max={8} maxLength={60} disabled={locked}
                            placeholder="+7 (900) 111-11-11" addLabel="Телефон" onChange={(phones) => patch({ phones })} />
                        <StringList label="Почты" values={draft.emails} max={6} disabled={locked}
                            placeholder="info@likpamyati.ru" addLabel="Почту" onChange={(emails) => patch({ emails })} />
                        <StringList label="Адреса" values={draft.addresses} max={4} maxLength={200} disabled={locked}
                            placeholder="г. Кировск, ул. Центральная, д. 10" addLabel="Адрес" onChange={(addresses) => patch({ addresses })} />
                        <Field label="Сайт" value={draft.site} maxLength={120} disabled={locked}
                            placeholder="likpamyati.ru" onChange={(site) => patch({ site })} />
                    </Section>

                    <Section title="Режим работы" hint={`На вывеске: ${formatSchedule(draft.schedule).join(" · ")}`}>
                        <Field label="Заголовок" value={draft.hoursTitle} maxLength={40} disabled={locked}
                            placeholder="Режим работы:" onChange={(hoursTitle) => patch({ hoursTitle })} />
                        <ScheduleEditor schedule={draft.schedule} disabled={locked} onChange={(schedule) => patch({ schedule })} />
                    </Section>

                    <Section title="Наши услуги" hint="Иконка + название. Иконки — встроенные (красятся под дизайн) или свои из библиотеки.">
                        <Field label="Заголовок блока" value={draft.servicesTitle} maxLength={60} disabled={locked}
                            placeholder="Наши услуги" onChange={(servicesTitle) => patch({ servicesTitle })} />
                        <ItemsEditor items={draft.services} meta={meta} iconAssets={iconAssets} max={MAX_SERVICES} disabled={locked}
                            addLabel="Услугу" onChange={(services) => patch({ services })} />
                    </Section>

                    <Section title="Преимущества" hint="Заголовок и карточки «иконка + цифра + текст». Фото к блоку выбирается в самой вывеске.">
                        <Field label="Заголовок блока" value={draft.featuresTitle} maxLength={90} disabled={locked}
                            placeholder="Современное 3D-оформление заказов" onChange={(featuresTitle) => patch({ featuresTitle })} />
                        <ItemsEditor items={draft.features} meta={meta} iconAssets={iconAssets} withValue max={MAX_FEATURES} disabled={locked}
                            addLabel="Карточку" onChange={(features) => patch({ features })} />
                    </Section>

                    <Section title="QR-код и кнопка">
                        <Field label="Ссылка в QR (пусто — сайт)" value={draft.qrUrl} maxLength={300} disabled={locked}
                            placeholder="https://alotb.ru" onChange={(qrUrl) => patch({ qrUrl })} />
                        <Field label="Подпись у QR" value={draft.qrCaption} maxLength={120} disabled={locked}
                            placeholder="Сканируйте для перехода в онлайн-офис" onChange={(qrCaption) => patch({ qrCaption })} />
                        <div className="printCatalogs__label">Кнопка внизу</div>
                        <div className="printSigns__itemRow">
                            <IconPicker value={{ icon: draft.buttonIcon, assetId: null }} meta={meta} disabled={locked}
                                onChange={(value) => patch({ buttonIcon: value.icon })} />
                            <input type="text" value={draft.buttonText} maxLength={40} disabled={locked} placeholder="Оформить заказ"
                                onChange={(event) => patch({ buttonText: event.target.value })} />
                        </div>
                    </Section>

                    <div className="printSigns__dangerRow">
                        <button type="button" disabled={locked} onClick={copy}>{busy === "copy" ? "Копируем…" : "Сделать копию"}</button>
                        <button type="button" className="printCatalogs__delete" disabled={locked} onClick={remove}>
                            {busy === "delete" ? "Удаление…" : "Удалить профиль"}
                        </button>
                    </div>
                </div>

                <div className="printCatalogs__actions">
                    {message ? <div className={`printCatalogs__message is-${message.type}`}>{message.text}</div> : null}
                    <div className="printCatalogs__buttons">
                        <button type="button" className="is-primary" disabled={locked || !dirty} onClick={save}>
                            {busy === "save" ? "Сохранение…" : dirty ? "Сохранить профиль" : "Сохранено"}
                        </button>
                        <button type="button" disabled={preview.loading} onClick={preview.refresh}>Предпросмотр</button>
                    </div>
                </div>
            </div>

            <PreviewPane
                url={preview.url}
                loading={preview.loading}
                error={preview.error}
                warnings={preview.warnings}
                autoPreview={preview.auto}
                onAutoChange={preview.setAuto}
                onRefresh={preview.refresh}
                subtitle={previewSignId ? "На вывеске, где стоит этот профиль" : "На вывеске по умолчанию (своих вывесок с этим профилем пока нет)"}
            />
        </>
    );
};

export default ProfileEditor;
